"use client";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Plus, Calendar, List, CheckCircle, Check, Pencil, Trash2,
  Phone, ClipboardList, ShoppingCart, Sparkles, ChevronDown, X,
  Search, RotateCcw, User
} from "lucide-react";
import moment from "moment";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import {
  getCategories,
  fetchTasks,
  createTask,
  updateTask,
  toggleTask,
  deleteTask,
  bulkDeleteTasks,
} from "../apis/taskService";
import { sessionService } from "../apis/sessionService";

const FALLBACK_CATEGORIES = ["Order", "Coordinate", "Communicate", "Document", "Action"];

function FloatingCategoryMenu({
  open,
  anchorRect,
  categories,
  currentCategory,
  onSelect,
  onClose,
  renderIcon,
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (!menuRef.current?.contains(e.target)) onClose?.();
    };
    const onScrollOrResize = () => onClose?.();
    document.addEventListener("mousedown", onClick, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onClick, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  const GAP = 8;
  const menuWidth = 220;
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - menuWidth - 8));
  const top = Math.min(anchorRect.bottom + GAP, window.innerHeight - 8 - 44);

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: "fixed", top, left, width: menuWidth }}
      className="z-[9999] bg-white border shadow-lg rounded-md overflow-auto max-h-[60vh]"
      onClick={(e) => e.stopPropagation()}
    >
      {categories.map((cat) => {
        const active = currentCategory === cat;
        return (
          <button
            key={cat}
            className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${active ? "font-semibold" : ""}`}
            onClick={() => onSelect(cat)}
          >
            <span className="flex items-center gap-2">
              {renderIcon(cat)}
              {cat}
            </span>
            {active && <Check className="w-4 h-4 text-blue-500" />}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

function Pagination({ page, limit, total, onPageChange, onLimitChange }) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const windowSize = 1;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - windowSize && p <= page + windowSize)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(total, page * limit);

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4">
      <div className="text-sm text-gray-600">
        Showing <span className="font-medium">{start}</span>–<span className="font-medium">{end}</span> of <span className="font-medium">{total}</span>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page</span>
          <select
            className="border rounded-md px-2 py-1 text-sm"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1">
          <button
            className={`px-2 py-1 border rounded-md text-sm ${canPrev ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"}`}
            onClick={() => canPrev && onPageChange(page - 1)}
            aria-label="Previous page"
          >
            Prev
          </button>

          {pages.map((p, i) => (
            <button
              key={`${p}-${i}`}
              disabled={p === "…"}
              className={`px-3 py-1 border rounded-md text-sm ${p === page ? "bg-gray-900 text-white border-gray-900" : p === "…" ? "cursor-default" : "hover:bg-gray-50"}`}
              onClick={() => typeof p === "number" && onPageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className={`px-2 py-1 border rounded-md text-sm ${canNext ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"}`}
            onClick={() => canNext && onPageChange(page + 1)}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TaskPage() {
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTask, setNewTask] = useState({ title: "", category: "", status: "To Do" });

  // Patient dropdown in modal
  const [patientOptions, setPatientOptions] = useState([]); // [{id, label, raw}]
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientQuery, setPatientQuery] = useState("");
  const [patientOpen, setPatientOpen] = useState(false);
  const [latestSessionPatientId, setLatestSessionPatientId] = useState("");

  // Filters
  const [filter, setFilter] = useState({ status: "All", category: "All", date: null });
  const [openFilter, setOpenFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rowFade, setRowFade] = useState({});
  const [dateRange, setDateRange] = useState(undefined);

  // Patient filter dropdown
  const [patientFilterId, setPatientFilterId] = useState("");
  const [patientFilterQuery, setPatientFilterQuery] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const serializedDate = useMemo(() => JSON.stringify(filter.date), [filter.date]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter.status, filter.category, serializedDate, patientFilterId]);

  const [selected, setSelected] = useState(new Set());
  const allSelected = tasks.length > 0 && selected.size === tasks.length;
  const toggleRowSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) => (prev.size === tasks.length ? new Set() : new Set(tasks.map((t) => t.id))));
  useEffect(() => {
    setSelected((prev) => {
      const keep = new Set();
      const ids = new Set(tasks.map((t) => t.id));
      prev.forEach((id) => ids.has(id) && keep.add(id));
      return keep;
    });
  }, [tasks]);

  const filterRef = useRef();
  useEffect(() => {
    const handle = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setOpenFilter("");
    };
    if (openFilter) {
      window.addEventListener("mousedown", handle);
      return () => window.removeEventListener("mousedown", handle);
    }
  }, [openFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = useMemo(() => {
    const params = { sortBy: "createdAt", sortOrder: "desc", page, limit };
    if (filter.status !== "All") params.status = filter.status;
    if (filter.category !== "All") params.category = filter.category;
    if (debouncedSearch) params.search = debouncedSearch;
    if (filter.date) {
      if (Array.isArray(filter.date)) {
        const [from, to] = filter.date;
        if (from && to) {
          params.dateFrom = moment(from).format("YYYY-MM-DD");
          params.dateTo = moment(to).format("YYYY-MM-DD");
        }
      } else {
        params.date = moment(filter.date).format("YYYY-MM-DD");
      }
    }
    if (patientFilterId) params.patientId = patientFilterId;
    return params;
  }, [filter, debouncedSearch, page, limit, patientFilterId]);

  // Load categories
  useEffect(() => {
    (async () => {
      try {
        const data = await getCategories();
        if (Array.isArray(data) && data.length) setCategories(data);
      } catch {
        setCategories(FALLBACK_CATEGORIES);
      }
    })();
  }, []);

  // Build patient options from sessions
  useEffect(() => {
    (async () => {
      try {
        const payload = await sessionService.getUserSessions();
        const sessions = payload?.data?.sessions ?? [];

        // Dedupe by patient.id; keep latest by createdAt
        const map = new Map();
        sessions.forEach((s) => {
          const p = s?.patient;
          if (!p || !p.id) return;
          const ts = new Date(s.createdAt || 0).getTime();
          const prev = map.get(p.id);
          if (!prev || ts > prev.ts) map.set(p.id, { p, ts });
        });

        const options = Array.from(map.values()).map(({ p }) => ({
          id: String(p.id),
          label: formatPatientLabel(p),
          raw: p,
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        setPatientOptions(options);

        // latest patient across sessions (for modal default)
        const latest = [...sessions]
          .filter((s) => s?.patient?.id)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.patient;
        if (latest?.id) {
          const latestId = String(latest.id);
          setLatestSessionPatientId(latestId);
          // Seed modal only when opening; filter has its own control
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const data = await fetchTasks(queryParams);
      const items = Array.isArray(data?.items) ? data.items : [];
      setTasks(items);
      setTotal(typeof data?.total === "number" ? data.total : items.length);
      if (typeof data?.limit === "number") setLimit(data.limit);
      if (typeof data?.page === "number") setPage(data.page);
    } catch (e) {
      setErrMsg(e.message || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const categoryIcon = (cat) => {
    const lookup = {
      Order: <ShoppingCart className="w-4 h-4 text-pink-500" />,
      Coordinate: <Calendar className="w-4 h-4 text-purple-600" />,
      Communicate: <Phone className="w-4 h-4 text-orange-500" />,
      Document: <ClipboardList className="w-4 h-4 text-green-600" />,
      Action: <Sparkles className="w-4 h-4 text-blue-600" />,
    };
    return lookup[cat] || null;
  };

  // ===== Modal: patient dropdown (search + select) =====
  const patientRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (patientRef.current && !patientRef.current.contains(e.target)) setPatientOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setPatientOpen(false);
    document.addEventListener("mousedown", onClickOutside, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside, true);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return patientOptions;
    return patientOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [patientQuery, patientOptions]);

  const handleChoosePatient = (opt) => {
    setSelectedPatientId(opt?.id || "");
    setPatientQuery(opt?.label || "");
    setPatientOpen(false);
  };

  const clearPatient = () => {
    setSelectedPatientId("");
    setPatientQuery("");
  };

  // ===== Filter: patient dropdown (search + select) =====
  const patientFilterRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (patientFilterRef.current && !patientFilterRef.current.contains(e.target)) {
        if (openFilter === "patient") setOpenFilter("");
      }
    };
    document.addEventListener("mousedown", onClickOutside, true);
    return () => document.removeEventListener("mousedown", onClickOutside, true);
  }, [openFilter]);

  const filteredPatientFilterOptions = useMemo(() => {
    const q = patientFilterQuery.trim().toLowerCase();
    if (!q) return patientOptions;
    return patientOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [patientFilterQuery, patientOptions]);

  const currentPatientFilterLabel = useMemo(() => {
    if (!patientFilterId) return "All";
    return patientOptions.find((o) => o.id === patientFilterId)?.label || "Selected";
  }, [patientFilterId, patientOptions]);

  // ===== CRUD handlers =====
  const handleSaveTask = async () => {
    if (!newTask.title.trim() || !newTask.category) return;
    try {
      if (editingId) {
        const body = {
          title: newTask.title,
          category: newTask.category,
          status: newTask.status,
          ...(selectedPatientId ? { patientId: selectedPatientId } : {}), // editable patient on update
        };
        const updated = await updateTask(editingId, body);
        setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await createTask({
          title: newTask.title,
          category: newTask.category,
          status: newTask.status,
          ...(selectedPatientId ? { patientId: selectedPatientId } : {}),
        });
        setTasks((prev) => [created, ...prev].slice(0, limit));
        setTotal((n) => n + 1);
      }
      closeModal();
    } catch (e) {
      setErrMsg(e.message || "Failed to save task");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setNewTask({ title: "", category: "", status: "To Do" });
    if (latestSessionPatientId) {
      const opt = patientOptions.find((o) => o.id === latestSessionPatientId);
      setSelectedPatientId(latestSessionPatientId);
      setPatientQuery(opt?.label || "");
    } else {
      setSelectedPatientId("");
      setPatientQuery("");
    }
    setShowModal(true);
    setCategoryOpen(false);
  };

  const openEdit = (task) => {
    setEditingId(task.id);
    setNewTask({ title: task.title, category: task.category, status: task.status });

    // Pre-fill with task's current patient (editable now)
    const p = task.patient;
    if (p?.patientId || p?.id || p?.PatientId) {
      const pid = String(p.patientId ?? p.id ?? p.PatientId);
      const label = formatPatientLabel({
        id: pid,
        patientNumber: p.patientNumber ?? null,
        name: p.name ?? [p.firstName, p.lastName].filter(Boolean).join(" "),
      });
      setSelectedPatientId(pid);
      setPatientQuery(label);
    } else {
      setSelectedPatientId("");
      setPatientQuery("");
    }

    setShowModal(true);
    setCategoryOpen(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setCategoryOpen(false);
    setEditingId(null);
    setNewTask({ title: "", category: "", status: "To Do" });
    setPatientOpen(false);
  };

  const handleDeleteTask = async (id) => {
    const prev = tasks;
    setTasks((t) => t.filter((x) => x.id !== id));
    setTotal((n) => Math.max(0, n - 1));
    try {
      await deleteTask(id);
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      const pageHasItems = (tasks.length - 1) > 0;
      if (!pageHasItems && page > 1) setPage((p) => p - 1);
    } catch (e) {
      setErrMsg(e.message || "Delete failed");
      setTasks(prev);
      setTotal((n) => n + 1);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const prev = tasks;
    const remaining = prev.filter((x) => !selected.has(x.id));
    setTasks(remaining);
    setSelected(new Set());
    setTotal((n) => Math.max(0, n - ids.length));
    try {
      await bulkDeleteTasks(ids);
      if (remaining.length === 0 && page > 1) setPage((p) => p - 1);
    } catch (e) {
      setErrMsg(e.message || "Bulk delete failed");
      setTasks(prev);
      setTotal((n) => n + ids.length);
    }
  };

  const handleToggleTaskDone = async (id) => {
    if (rowFade[id]) return;
    setRowFade((p) => ({ ...p, [id]: "fadeout" }));
    try {
      const updated = await toggleTask(id);
      setTimeout(() => {
        setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
        setRowFade((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, 1000);
    } catch (e) {
      setErrMsg(e.message || "Toggle failed");
      setRowFade((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const [catOpen, setCatOpen] = useState(null);
  const closeCatMenu = useCallback(() => setCatOpen(null), []);
  const openCatMenu = (taskId, rect) => setCatOpen({ id: taskId, rect });

  const handleSelectCategoryInline = async (taskId, newCat) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.category === newCat) return closeCatMenu();
    const prev = tasks;
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, category: newCat } : t)));
    closeCatMenu();
    try {
      const updated = await updateTask(taskId, { category: newCat });
      setTasks((ts) => ts.map((t) => (t.id === taskId ? updated : t)));
    } catch (e) {
      setErrMsg(e.message || "Failed to update category");
      setTasks(prev);
    }
  };

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      setFilter((prev) => ({ ...prev, date: [dateRange.from, dateRange.to] }));
      setOpenFilter("");
    }
  }, [dateRange]);

  const renderPatientBadge = (task) => {
    const p = task.patient;
    if (!p) return <span className="text-xs text-gray-400 italic">No patient</span>;
    const name =
      [p.firstName, p.lastName].filter(Boolean).join(" ") ||
      (p.name ? String(p.name).trim() : "") ||
      "Unknown";
    const num = p.patientNumber ? ` · #${p.patientNumber}` : "";
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200">
        <User className="w-3 h-3" />
        <span>{name}{num}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 px-6 py-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-2xl font-bold">Tasks</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleBulkDelete}
            disabled={selected.size === 0 || loading}
            className={`flex-1 sm:flex-none justify-center flex items-center gap-2 px-3 py-2 rounded-md border ${
              selected.size === 0 || loading
                ? "text-gray-400 border-gray-200 cursor-not-allowed"
                : "text-red-600 border-red-300 hover:bg-red-50"
            }`}
            title={selected.size ? `Delete ${selected.size} selected` : "Select rows to delete"}
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden xs:inline">Delete selected</span>
            {selected.size ? ` (${selected.size})` : ""}
          </button>

          <button
            onClick={openCreate}
            className="flex-1 sm:flex-none justify-center flex items-center gap-2 bg-[#0b1221] text-white px-4 py-2 rounded-md hover:bg-[#1a1f33]"
          >
            <Plus className="w-4 h-4" /> <span>New task</span>
          </button>
        </div>
      </div>

      {errMsg ? (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
          {errMsg}
        </div>
      ) : null}

      {/* Filters (now includes Patient dropdown) */}
      <div className="flex flex-wrap items-stretch md:items-center gap-3 md:gap-4 mb-6" ref={filterRef}>
        <div className="flex items-center border rounded-md bg-white px-3 py-2 w-full md:w-auto md:min-w-[320px]">
          <Search className="w-5 h-5 text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Search for a task"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="outline-none border-0 bg-transparent text-gray-700 w-full placeholder-gray-400 text-sm"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setOpenFilter(openFilter === "status" ? "" : "status")}
            className="flex items-center w-full sm:w-auto border rounded-lg bg-white px-3 py-2 gap-2 min-w-[140px]"
          >
            <CheckCircle className="w-5 h-5 text-black" />
            <span className="font-medium">Status</span>
            <span className="border-l h-5 mx-2" />
            <span className="px-2 py-1 rounded bg-gray-100 text-sm font-medium">{filter.status}</span>
          </button>
          {openFilter === "status" && (
            <div className="absolute mt-2 left-0 w-48 bg-white border shadow-lg rounded-lg z-20">
              {["All", "To Do", "Done"].map((status) => (
                <div
                  key={status}
                  className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer justify-between ${filter.status === status ? "font-semibold" : ""}`}
                  onClick={() => {
                    setFilter({ ...filter, status });
                    setOpenFilter("");
                  }}
                >
                  <span>{status}</span>
                  {filter.status === status && <Check className="w-4 h-4 text-blue-500" />}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setOpenFilter(openFilter === "category" ? "" : "category")}
            className="flex items-center w-full sm:w-auto border rounded-lg bg-white px-3 py-2 gap-2 min-w-[160px]"
          >
            <List className="w-5 h-5 text-black" />
            <span className="font-medium">Category</span>
            <span className="border-l h-5 mx-2" />
            <span className="px-2 py-1 rounded bg-gray-100 text-sm font-medium">{filter.category}</span>
          </button>
          {openFilter === "category" && (
            <div className="absolute mt-2 left-0 w-56 bg-white border shadow-lg rounded-lg z-20">
              {["All", ...categories].map((cat) => (
                <div
                  key={cat}
                  className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer justify-between ${filter.category === cat ? "font-semibold" : ""}`}
                  onClick={() => {
                    setFilter({ ...filter, category: cat });
                    setOpenFilter("");
                  }}
                >
                  <span className="flex items-center gap-2">
                    {categoryIcon(cat)}
                    {cat}
                  </span>
                  {filter.category === cat && <Check className="w-4 h-4 text-blue-500" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Patient Filter */}
        <div className="relative" ref={patientFilterRef}>
          <button
            onClick={() => setOpenFilter(openFilter === "patient" ? "" : "patient")}
            className="flex items-center w-full sm:w-auto border rounded-lg bg-white px-3 py-2 gap-2 min-w-[220px]"
          >
            <User className="w-5 h-5 text-black" />
            <span className="font-medium">Patient</span>
            <span className="border-l h-5 mx-2" />
            <span className="px-2 py-1 rounded bg-gray-100 text-sm font-medium truncate max-w-[180px]">
              {currentPatientFilterLabel}
            </span>
          </button>

          {openFilter === "patient" && (
            <div className="absolute mt-2 left-0 w-[320px] bg-white border shadow-lg rounded-lg z-20">
              <div className="p-2 border-b">
                <input
                  type="text"
                  value={patientFilterQuery}
                  onChange={(e) => setPatientFilterQuery(e.target.value)}
                  placeholder="Search patients"
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              <div className="max-h-64 overflow-auto">
                <div
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${!patientFilterId ? "font-semibold" : ""}`}
                  onClick={() => { setPatientFilterId(""); setOpenFilter(""); }}
                >
                  All
                </div>
                {filteredPatientFilterOptions.map((opt) => (
                  <div
                    key={opt.id}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${patientFilterId === opt.id ? "bg-gray-100" : ""}`}
                    onClick={() => { setPatientFilterId(opt.id); setOpenFilter(""); }}
                  >
                    {opt.label}
                  </div>
                ))}
                {filteredPatientFilterOptions.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setOpenFilter(openFilter === "date" ? "" : "date")}
            className="flex items-center w-full sm:w-auto border rounded-lg bg-white px-3 py-2 gap-2 min-w-[170px]"
          >
            <Calendar className="w-5 h-5 text-black" />
            <span className="font-medium">Date</span>
            <span className="border-l h-5 mx-2" />
            <span className="px-2 py-1 rounded bg-gray-100 text-sm font-medium">
              {filter.date
                ? Array.isArray(filter.date)
                  ? `${moment(filter.date[0]).format("MMM D, YYYY")} - ${moment(filter.date[1]).format("MMM D, YYYY")}`
                  : moment(filter.date).format("MMM D, YYYY")
                : "All"}
            </span>
          </button>
          {openFilter === "date" && (
            <div className="absolute mt-2 left-0 w-[350px] bg-white border shadow-lg rounded-lg z-20 p-6">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                defaultMonth={new Date()}
                showOutsideDays
                className="mb-4"
                numberOfMonths={1}
                footer={
                  <div className="flex items-center justify-end pr-2">
                    <button
                      onClick={() => {
                        setDateRange(undefined);
                        setFilter((prev) => ({ ...prev, date: null }));
                        setOpenFilter("");
                      }}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Reset
                    </button>
                  </div>
                }
              />
            </div>
          )}
        </div>

        <button
          onClick={() => {
            setFilter({ status: "All", category: "All", date: null });
            setDateRange(undefined);
            setSearch("");
            setPatientFilterId("");
            setPatientFilterQuery("");
            setOpenFilter("");
            setPage(1);
          }}
          className="text-sm text-gray-500 ml-2 flex items-center gap-1 hover:underline"
        >
          Reset filters
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center text-gray-500 mt-20">
          <ClipboardList className="w-12 h-12 mb-4 text-gray-400" />
          <h2 className="text-lg font-semibold">No tasks yet</h2>
          <p className="mb-6 text-sm">Tasks saved from your sessions will appear here.</p>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-[#0b1221] text-white px-4 py-2 rounded-md hover:bg-[#1a1f33]"
          >
            <Plus className="w-4 h-4" /> Create a new task
          </button>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {tasks.map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-lg shadow-sm border p-3 ${rowFade[task.id] === "fadeout" ? "opacity-0" : "opacity-100"}`}
                onClick={() => handleToggleTaskDone(task.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      className={`w-6 h-6 flex items-center justify-center border-2 rounded-full ${task.status === "Done" ? "border-green-500 bg-green-500 text-white" : "border-gray-300 bg-white text-gray-300"}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleTaskDone(task.id); }}
                      aria-label={task.status === "Done" ? "Mark as To Do" : "Mark as Done"}
                    >
                      {task.status === "Done" ? <Check className="w-4 h-4" /> : null}
                    </button>
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{task.title}</div>
                      <div className="text-gray-500 mt-0.5">{moment(task.createdAt).fromNow()}</div>
                      <div className="mt-2 inline-flex items-center gap-2 text-xs bg-gray-100 px-2 py-1 rounded">
                        {categoryIcon(task.category)}
                        <span>{task.category || "No category"}</span>
                      </div>

                      <div className="mt-2">{renderPatientBadge(task)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(task); }} aria-label="Edit task">
                      <Pencil className="w-4 h-4 text-blue-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }} aria-label="Delete task">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                    <input
                      type="checkbox"
                      className="ml-1"
                      checked={selected.has(task.id)}
                      onChange={(e) => { e.stopPropagation(); toggleRowSelect(task.id); }}
                      aria-label={`Select task ${task.id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className="bg-white rounded-lg shadow-sm border">
              <Pagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(n) => { setLimit(n); setPage(1); }}
              />
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto bg-white rounded-md shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left font-medium">
                <tr>
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => { e.stopPropagation(); toggleAll(); }}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-4 py-3">Task title</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Category</th>
                  <th className="px-4 py-3 text-center">Patient</th>
                  <th className="px-4 py-3 text-center">Created</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr
                    key={task.id}
                    onClick={() => handleToggleTaskDone(task.id)}
                    className={`border-t hover:bg-gray-50 transition-opacity duration-[1000ms] cursor-pointer ${rowFade[task.id] === "fadeout" ? "opacity-0" : "opacity-100"}`}
                  >
                    <td className="px-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(task.id)}
                        onChange={() => toggleRowSelect(task.id)}
                        aria-label={`Select task ${task.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-5 h-5 flex items-center justify-center border-2 rounded-full transition-all cursor-pointer circle-toggle ${task.status === "Done" ? "border-green-500 bg-green-500 text-white" : "border-gray-300 bg-white text-gray-300"}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleTaskDone(task.id); }}
                          tabIndex={0}
                          role="button"
                          aria-label={task.status === "Done" ? "Mark as To Do" : "Mark as Done"}
                        >
                          {task.status === "Done" ? <Check className="w-4 h-4" /> : null}
                        </span>
                        <span>{task.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${task.status === "Done" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-800"}`}>{task.status}</span>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); openCatMenu(task.id, rect); }}
                        className="inline-flex items-center justify-between gap-2 text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                        title="Change category"
                      >
                        <span className="flex items-center gap-2">
                          {categoryIcon(task.category)}
                          {task.category || "Select category"}
                        </span>
                        <ChevronDown className="w-3 h-3 text-gray-500" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">{renderPatientBadge(task)}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{moment(task.createdAt).fromNow()}</td>
                    <td className="px-4 py-3 flex gap-4 justify-center" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEdit(task)}>
                        <Pencil className="w-4 h-4 text-blue-500" />
                      </button>
                      <button onClick={() => handleDeleteTask(task.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              limit={limit}
              total={total}
              onPageChange={(p) => setPage(p)}
              onLimitChange={(n) => { setLimit(n); setPage(1); }}
            />
          </div>
        </>
      )}

      <FloatingCategoryMenu
        open={!!catOpen}
        anchorRect={catOpen?.rect || null}
        categories={categories}
        currentCategory={catOpen ? tasks.find((t) => t.id === catOpen.id)?.category : null}
        onSelect={(cat) => handleSelectCategoryInline(catOpen.id, cat)}
        onClose={closeCatMenu}
        renderIcon={categoryIcon}
      />

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl relative">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{editingId ? "Edit task" : "Create task"}</h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask((s) => ({ ...s, title: e.target.value }))}
                  placeholder="Enter title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCategoryOpen((o) => !o)}
                    className="w-full flex justify-between items-center px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                  >
                    <span className="flex items-center gap-2">
                      <List className="w-4 h-4" />
                      {newTask.category || "Select category"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                  {categoryOpen && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-md max-h-72 overflow-auto">
                      {categories.map((label) => (
                        <div
                          key={label}
                          onClick={() => {
                            setNewTask((s) => ({ ...s, category: label }));
                            setCategoryOpen(false);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
                        >
                          {categoryIcon(label)}
                          {label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Patient dropdown (editable on create + edit) */}
              <div ref={patientRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient (from sessions)
                </label>
                <div className="relative">
                  <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-white">
                    <User className="w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={patientQuery}
                      onChange={(e) => {
                        setPatientQuery(e.target.value);
                        setSelectedPatientId(""); // clear selection while typing
                        setPatientOpen(true);
                      }}
                      onFocus={() => setPatientOpen(true)}
                      placeholder="Type to search (e.g., #123 - John Doe)"
                      className="flex-1 outline-none bg-transparent text-sm"
                    />
                    {selectedPatientId ? (
                      <button
                        type="button"
                        onClick={clearPatient}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        title="Clear patient"
                      >
                        Clear
                      </button>
                    ) : null}
                    <ChevronDown
                      className="w-4 h-4 text-gray-500 cursor-pointer"
                      onClick={() => setPatientOpen((o) => !o)}
                      title="Open patient list"
                    />
                  </div>

                  {patientOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-md max-h-64 overflow-auto">
                      {filteredPatients.length > 0 ? (
                        filteredPatients.map((opt) => (
                          <div
                            key={opt.id}
                            onMouseDown={() => handleChoosePatient(opt)}
                            className={`px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer ${selectedPatientId === opt.id ? "bg-gray-100" : ""}`}
                          >
                            {opt.label}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Format: <code>#patientNumber - Patient Name</code>. If no number, only the name is shown.</p>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTask}
                className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm hover:bg-gray-900"
              >
                {editingId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatPatientLabel(p) {
  const num = p?.patientNumber ? `#${p.patientNumber}` : "";
  const name = (p?.name ? String(p.name).trim() : "").trim();
  return [num, name].filter(Boolean).join(" - ") || "Unnamed patient";
}
